import React, { useState, useEffect } from "react";
import { Card, CardBody, CardHeader } from "../card/card";
import ConfirmationDlg from "../ConfirmationDlg";
import NormalInput from "../form/HorizontalNormalInput";
import NormalSelect from "../form/HorizontalNormalSelect";
import { user, workOrderTimeTrackingType } from "../../api";
import { toast } from "react-toastify";
import { formatDateFull } from "../../utils/helper";
import { useAuth } from "../../contexts/AuthContext";

const TimeTracking = ({ value = [], onChange, readonly = false }) => {
  const { config } = useAuth();
  const [timeEntries, setTimeEntries] = useState(value);
  const [showAddModal, setShowAddModal] = useState(false);
  const [users, setUsers] = useState([]);
  const [timeTypes, setTimeTypes] = useState([]);
  const [newTimeEntry, setNewTimeEntry] = useState({
    user: "",
    hours: "",
    minutes: "",
    type: "",
    hourly_rate: "0",
  });
  const [newTimeType, setNewTimeType] = useState("");
  const [showAddTypeModal, setShowAddTypeModal] = useState(false);

  useEffect(() => {
    if (value.length > 0) {
      let newEntries = [];
      value.forEach((entry) => {
        const totalMinutes =
          (parseInt(entry.hours) || 0) * 60 + (parseInt(entry.minutes) || 0);

        const newEntry = {
          user: entry.user,
          hours: parseInt(entry.hours) || 0,
          minutes: parseInt(entry.minutes) || 0,
          total_minutes: totalMinutes,
          type: entry.type,
          hourly_rate: parseFloat(entry.hourly_rate) || 0,
          created_at: entry.created_at,
        };

        newEntries.push(newEntry);
      });

      console.log(newEntries);

      setTimeEntries(newEntries);
    }
  }, [value]);

  useEffect(() => {
    if (showAddModal) {
      getUsers();
      getTimeTypes();
    }
  }, [showAddModal]);

  const getUsers = async () => {
    try {
      const res = await user.list({});
      if (res.ok) {
        const body = await res.json();
        let data = body.data;
        if (data) {
          data = data.map((d) => ({
            username: d.username,
            name: d.firstname + " " + d.lastname,
            value: d.username,
          }));
          setUsers(data);
        }
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const getTimeTypes = async () => {
    try {
      const res = await workOrderTimeTrackingType.list({});
      if (res.ok) {
        const body = await res.json();
        let data = body.data;
        if (data) {
          data = data.map((d) => ({
            id: d.id,
            name: d.name,
            value: d.id,
          }));
          setTimeTypes(data);
        }
      }
    } catch (error) {
      console.error("Error fetching time types:", error);
    }
  };

  const handleAddNewTimeType = async () => {
    if (!newTimeType) {
      toast.error("Please enter a time type name");
      return;
    }

    try {
      const result = await workOrderTimeTrackingType.create({
        name: newTimeType,
      });

      if (result.ok) {
        const body = await result.json();
        const data = body.data;
        setTimeTypes((prev) => [
          { id: data.id, name: data.name, value: data.id },
          ...prev,
        ]);
        setNewTimeType("");
        setShowAddTypeModal(false);
        toast.success("Time type added successfully");
      } else {
        const errorBody = await result.json();
        toast.error(errorBody.message || "Failed to add time type");
      }
    } catch (error) {
      console.error("Error adding time type:", error);
      toast.error("Failed to add time type");
    }
  };

  const handleAddTime = () => {
    if (!newTimeEntry.user) {
      toast.error("Please select a user");
      return;
    }
    if (!newTimeEntry.hours && !newTimeEntry.minutes) {
      toast.error("Please enter hours or minutes");
      return;
    }
    if (!newTimeEntry.type) {
      toast.error("Please select a time type");
      return;
    }

    const hours = parseInt(newTimeEntry.hours) || 0;
    const minutes = parseInt(newTimeEntry.minutes) || 0;
    const hourlyRate = parseFloat(newTimeEntry.hourly_rate) || 0;
    if (hours < 0 || minutes < 0 || hourlyRate < 0) {
      toast.error("Hours, minutes, and hourly rate cannot be negative");
      return;
    }

    const totalMinutes = hours * 60 + minutes;
    if (totalMinutes === 0) {
      toast.error("Please enter a valid time");
      return;
    }

    const entry = {
      user: users.find((u) => u.value === newTimeEntry.user),
      hours: hours,
      minutes: minutes,
      total_minutes: totalMinutes,
      type: timeTypes.find((t) => t.value === newTimeEntry.type),
      hourly_rate: hourlyRate,
      created_at: new Date().toISOString(),
    };

    const updatedEntries = [...timeEntries, entry];
    setTimeEntries(updatedEntries);
    onChange(updatedEntries);
    setShowAddModal(false);
    setNewTimeEntry({
      user: "",
      hours: "",
      minutes: "",
      type: "",
      hourly_rate: "0",
    });
    setNewTimeType("");
  };

  const handleDeleteTime = (index) => {
    const updatedEntries = timeEntries.filter((_, i) => i !== index);
    setTimeEntries(updatedEntries);
    onChange(updatedEntries);
  };

  const formatTime = (hours, minutes) => {
    const totalMinutes = hours * 60 + minutes;
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    return `${h}h ${m}m`;
  };

  const calculateTotalTime = () => {
    return timeEntries.reduce((total, entry) => {
      return total + entry.total_minutes;
    }, 0);
  };

  const calculateTotalCost = () => {
    return timeEntries.reduce((total, entry) => {
      const hours = entry.total_minutes / 60;
      return total + hours * entry.hourly_rate;
    }, 0);
  };

  const totalTime = calculateTotalTime();
  const totalCost = calculateTotalCost();

  return (
    <Card style={{ marginTop: "10px" }}>
      <CardHeader>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>Time Tracking</div>
          {!readonly && (
            <button
              onClick={() => setShowAddModal(true)}
              className="btn btn-sm btn-outline-theme"
            >
              Add Time
            </button>
          )}
        </div>
      </CardHeader>
      <CardBody>
        {timeEntries.length > 0 ? (
          <div className="table-responsive">
            <table className="table table-striped">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Type</th>
                  <th>Time</th>
                  <th>Rate</th>
                  <th>Cost</th>
                  <th>Date</th>
                  {!readonly && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {timeEntries.map((entry, index) => (
                  <tr key={entry.id || index}>
                    <td>
                      {entry.user?.name ||
                        entry.user?.firstname + " " + entry.user?.lastname}
                    </td>
                    <td>
                      <span className={`badge bg-primary`}>
                        {entry.type?.name}
                      </span>
                    </td>
                    <td>{formatTime(entry.hours, entry.minutes)}</td>
                    <td>
                      {config?.currency_symbol}
                      {entry.hourly_rate}/hr
                    </td>
                    <td>
                      {config?.currency_symbol}
                      {((entry.total_minutes / 60) * entry.hourly_rate).toFixed(
                        2
                      )}
                    </td>
                    <td>{formatDateFull(entry.created_at)}</td>
                    {!readonly && (
                      <td>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleDeleteTime(index)}
                        >
                          Delete
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="table-info">
                  <td colSpan="2">
                    <strong>Total</strong>
                  </td>
                  <td>
                    <strong>
                      {formatTime(Math.floor(totalTime / 60), totalTime % 60)}
                    </strong>
                  </td>
                  <td></td>
                  <td>
                    <strong>
                      {config?.currency_symbol}
                      {totalCost.toFixed(2)}
                    </strong>
                  </td>
                  <td colSpan={readonly ? 1 : 2}></td>
                </tr>
              </tfoot>
            </table>
          </div>
        ) : (
          <div className="text-center text-muted py-4">
            <i className="fa fa-clock fa-3x mb-3"></i>
            <p>No time entries yet.</p>
          </div>
        )}

        {/* Add Time Modal */}
        <ConfirmationDlg
          open={showAddModal}
          onClose={() => setShowAddModal(false)}
          onSubmit={handleAddTime}
          title="Add Time"
          btnSubmitText="Add"
          btnCancelText="Cancel"
          size="md"
        >
          <div className="row g-3">
            <div className="col-12">
              <NormalSelect
                label="User"
                value={newTimeEntry.user}
                onchange={(e) =>
                  setNewTimeEntry({ ...newTimeEntry, user: e.target.value })
                }
                list={users}
                valueKey="value"
                required
                emptyField={[]}
              />
            </div>

            <div className="col-6">
              <NormalInput
                label="Hours"
                type="number"
                value={newTimeEntry.hours}
                onchange={(e) =>
                  setNewTimeEntry({ ...newTimeEntry, hours: e.target.value })
                }
                name="hours"
                min={0}
                emptyField={[]}
              />
            </div>

            <div className="col-6">
              <NormalInput
                label="Minutes"
                type="number"
                value={newTimeEntry.minutes}
                onchange={(e) =>
                  setNewTimeEntry({ ...newTimeEntry, minutes: e.target.value })
                }
                name="minutes"
                min={0}
                emptyField={[]}
              />
            </div>

            <div className="col-12">
              <NormalInput
                label="Hourly Rate (Optional)"
                type="number"
                value={newTimeEntry.hourly_rate}
                onchange={(e) =>
                  setNewTimeEntry({
                    ...newTimeEntry,
                    hourly_rate: e.target.value,
                  })
                }
                name="hourly_rate"
                min={0}
                step="0.01"
                emptyField={[]}
                helperText="Enter hourly rate to calculate costs"
              />
            </div>

            <div className="col-12">
              <NormalSelect
                label="Type"
                value={newTimeEntry.type}
                onchange={(e) =>
                  setNewTimeEntry({ ...newTimeEntry, type: e.target.value })
                }
                list={timeTypes}
                valueKey="value"
                required
                emptyField={[]}
                onSubmit={handleAddNewTimeType}
                addMoreComponent={
                  <NormalInput
                    required={true}
                    value={newTimeType}
                    label="Time Type"
                    onchange={(e) => {
                      setNewTimeType(e.target.value);
                    }}
                  />
                }
              />
            </div>
          </div>
        </ConfirmationDlg>
      </CardBody>
    </Card>
  );
};

export default TimeTracking;
